import type { RouteRecordRaw } from 'vue-router';
import type { CurrentUser } from '@/core';
import { PERM } from '@/permissions';

/**
 * 路由表（《前端项目开发文档》§5.2 视图清单）。路由 **name** 逐字沿用文档标识
 * （bpoNew / bpoMine / bpoHelp），便于 PRD 验收条目与代码互相定位。
 * ⛔ 2026-10-10 用户裁定「整条驳回链下线」：`bpoReject`（/reject 被驳回页）路由已删除。
 *
 * `meta.perm` 是**菜单级**权限码，取值来自服务端 `GET /auth/profile` 的 `menuPermissions`
 * 快照（清单收口在 `@/permissions`，本文件不重复打字符串）。《前端文档》§7 只规定了
 * 「菜单级控页面、按钮级控操作」的落地方式，没有冻结码字符串，故以快照为唯一真源。
 * 守卫按服务端快照判定；**服务端同样会拒**（§13-A），前端的判定只是省一次往返
 * 与不给出无效入口，不是安全边界。
 */
export const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/login/LoginView.vue'),
    meta: { public: true, title: '登录' },
  },
  // t108（F11 完整链）：改密出口页。⛔ 不套 BpoShell（首登拦截期间业务骨架与录单入口一律不渲染）；
  // 非 meta.public ⇒ 无令牌仍先落登录页；已登录且未改密时守卫**只放行本页**。
  {
    path: '/change-password',
    name: 'changePassword',
    component: () => import('@/views/system/ChangePasswordView.vue'),
    meta: { title: '修改初始密码' },
  },
  {
    path: '/',
    component: () => import('@/layouts/BpoShell.vue'),
    children: [
      // 站点根默认落点：这是「没说去哪」的兜底，**不是**用户选定的落地页
      // ⇒ router/index.ts 的 toLogin 不回放这枚 redirect（to.redirectedFrom 非空即跳过），
      // 登录后由 homePathFor 按角色分流；录单员照旧落 /intake。
      { path: '', redirect: '/intake' },
      {
        path: 'intake',
        name: 'bpoNew',
        component: () => import('@/views/intake/IntakeView.vue'),
        meta: { title: '新建订单', crumb: '新建订单', perm: PERM.menuOrder, menu: true, menuOrder: 1 },
      },
      {
        path: 'mine',
        name: 'bpoMine',
        component: () => import('@/views/orders/MyOrdersView.vue'),
        meta: { title: '我的订单', crumb: '我的订单', perm: PERM.menuOrder, menu: true, menuOrder: 2 },
      },
      {
        path: 'orders/:no',
        name: 'bpoOrderDetail',
        component: () => import('@/views/orders/OrderDetailView.vue'),
        meta: { title: '订单详情', crumb: '订单详情', perm: PERM.menuOrder },
      },
      {
        path: 'help',
        name: 'bpoHelp',
        component: () => import('@/views/help/HelpView.vue'),
        meta: { title: '录入帮助', crumb: '录入帮助', perm: PERM.menuOrder, menu: true, menuOrder: 4 },
      },
      // ── BPO 机构管理员（v1.4 §1.6：共用入口、仓内路由级分流，无第二套部署）──
      {
        path: 'agency/orders',
        name: 'bpoAllOrders',
        component: () => import('@/views/agency/AllOrdersView.vue'),
        meta: { title: '全部订单', crumb: '全部订单', perm: PERM.menuBpoAllOrders, menu: true, menuOrder: 0 },
      },
      {
        path: 'agency/team',
        name: 'bpoTeam',
        component: () => import('@/views/agency/TeamView.vue'),
        meta: { title: '成员管理', crumb: '成员管理', perm: PERM.menuBpoTeam, menu: true, menuOrder: 0 },
      },
    ],
  },
  {
    // 独立公共页：不套 BpoShell，否则无令牌的访客也能看到内部导航骨架与水印
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/views/system/ForbiddenView.vue'),
    meta: { public: true, title: '无访问权限' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/system/NotFoundView.vue'),
    meta: { public: true, title: '页面不存在' },
  },
];

/** 登录成功后的落地页：录单端的存在意义就是录入，不做看板 */
export const HOME_PATH = '/intake';

/** 管理员落地页：v1.4 §1.7 登录后按角色分流，管理员先看机构全貌（全部订单） */
export const AGENCY_HOME_PATH = '/agency/orders';

/**
 * 角色落地页的唯一判据＝服务端快照里的机构菜单码（`menu:bpo_all_orders`），
 * ⛔ 按 `roleCode` 字符串硬分——分流跟着权限码走，B3 批落库前普通账号绝无此码，天然落回 `/intake`。
 */
export function homePathFor(user: CurrentUser | null): string {
  return user?.permissions.includes(PERM.menuBpoAllOrders) ? AGENCY_HOME_PATH : HOME_PATH;
}
